"use client";

import { useEffect, useState } from "react";

import { useServedBrand } from "./servedBrand";
import { BRAND_ATTRIBUTE, currentBrand, DEFAULT_BRAND, type BrandId } from "./tokens";

/**
 * Reactive read of the served brand off <html data-brand> — the attribute
 * the ExperienceProvider flips from the host-resolved experience. A
 * MutationObserver, not a one-time read: consumers must re-render when the
 * bootstrap resolves instead of freezing on the SSR default.
 *
 * #411: the FIRST render is seeded from ServedBrandContext when the server
 * resolved the brand, so a VIP host paints VIP chrome on the server and on
 * hydration — never the default brand for a frame. Without a served brand
 * (context null) the pre-#411 behaviour stands: default, then the observer.
 */
export function useCurrentBrand(): BrandId {
  const served = useServedBrand();
  const [id, setId] = useState<BrandId>(served ?? DEFAULT_BRAND);

  useEffect(() => {
    const root = document.documentElement;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time attribute sync on mount, then observer-driven
    setId(currentBrand(root));
    const observer = new MutationObserver(() => setId(currentBrand(root)));
    observer.observe(root, { attributes: true, attributeFilter: [BRAND_ATTRIBUTE] });
    return () => observer.disconnect();
  }, []);

  return id;
}
